import React from "react";
import axios from "axios";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { AgGridReact } from "ag-grid-react";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import {
  API_BASE_URL,
  API_URL_MAPPING,
  API_URL_MAPPING_SUGGESTIONS,
  API_URL_MAPPING_SAVE,
} from "../../configuration/Configuration";
import {
  menuItems,
  enrichTableColumns,
  isPosted,
} from "../../configuration/Utils";
import { useEffect } from "react";
import TableControls from "../../integrations/core/table/TableControls";
import { FullScreen, useFullScreenHandle } from "react-full-screen";

const Mapping = (props) => {
  const mappingFields = {
    0: "originCountry",
    1: "destinationCountry",
    2: "originCity",
    3: "destinationCity",
    4: "originZIP",
    5: "destinationZIP",
    6: "equipment",
    7: "payload",
    8: "transitTime",
    9: "palletExchange",
    10: "requirements",
    11: "oneway",
    12: "customerLaneID",
    13: "volume",
    14: "volumeUnit",
    15: "profitMargin",
    16: "additionalData",
    17: "capacity",
    18: "registerSheet",
  };
  const mappingFieldLabels = {
    originCountry: "Origin Country",
    destinationCountry: "Destination Country",
    originCity: "Origin City",
    destinationCity: "Destination City",
    originZIP: "Origin ZIP",
    destinationZIP: "Destination ZIP",
    equipment: "Equipment",
    payload: "Payload",
    transitTime: "Transit Time",
    palletExchange: "Pallet Exchange",
    requirements: "Special Requirements",
    oneway: "Oneway/Roundtrip",
    customerLaneID: "Lane ID provided by Customer",
    volume: "Annual Volume + Unit",
    volumeUnit: "Volume Unit",
    profitMargin: "Profit Margin",
    additionalData: "Custom Data",
    capacity: "Capacity",
    registerSheet: "Register Sheet",
  };

  const [selection, setSelection] = React.useState([]);
  const [fileStatus, setFileStatus] = React.useState(false);
  const [customColumnsMapping, setCustomColumnsMapping] = React.useState({});
  const [mappingSuggestions, setMappingSuggestions] = React.useState({});
  const [checkCol, setCheckCol] = React.useState({});
  const [loadingSuggestions, setLoadingSuggestions] = React.useState(false);
  const [suggestionsApplied, setSuggestionsApplied] = React.useState(false);
  const [suggestionsLoaded, setSuggestionsLoaded] = React.useState(false);
  const [showSuggestionsAlert, setShowSuggestionsAlert] = React.useState(false);
  const manuallyChangedCustomMappings = React.useRef(new Set());
  
  //LOAD TENDER DATA initially

  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/tender/${props.tender.tenderID.pk__max}`).then((res) => {
      if(res.data && res.data.length > 0){
        setCheckCol(res.data[0])
      }
    });
  }, []);
  useEffect(() => {
    let intSelection = props.uploadFile.columns.slice();
    intSelection = ["- no Mapping -"].concat(intSelection);
    setSelection(intSelection);
    setShowSuggestionsAlert(false);
    setSuggestionsApplied(false);
    setSuggestionsLoaded(false);
    setMappingSuggestions({});
  }, [props.uploadFile.columns]);

  React.useEffect(() => {
    window.addEventListener("beforeunload", handleUnload);

    return () => window.removeEventListener("beforeunload", handleUnload);
  }, []);

  // reset state before component leave
  const handleUnload = () => {
    setFileStatus(false);
  };

  const [var0, setVar0] = React.useState("");
  const [var1, setVar1] = React.useState("");
  const [var2, setVar2] = React.useState("- no Mapping -");
  const [var3, setVar3] = React.useState("- no Mapping -");
  const [var4, setVar4] = React.useState("");
  const [var5, setVar5] = React.useState("");
  const [var6, setVar6] = React.useState("- no Mapping -");
  const [var7, setVar7] = React.useState("- no Mapping -");
  const [var8, setVar8] = React.useState("- no Mapping -");
  const [var9, setVar9] = React.useState("- no Mapping -");
  const [var10, setVar10] = React.useState("- no Mapping -");
  const [var11, setVar11] = React.useState("- no Mapping -");
  const [var12, setVar12] = React.useState("- no Mapping -");
  const [var13, setVar13] = React.useState("");
  const [var14, setVar14] = React.useState("- no Mapping -");
  const [var15, setVar15] = React.useState("- no Mapping -");
  const [var16, setVar16] = React.useState("- no Mapping -");
  const [var17, setVar17] = React.useState("- no Mapping -");
  const [var18, setVar18] = React.useState("- no Mapping -");

  const [tableData, setTable] = React.useState([]);

  // Function to get all current variable values
  const getAllVarValues = () => {
    return [var0, var1, var2, var3, var4, var5, var6, var7, var8, var9, var10, var11, var12, var13, var14, var15, var16, var17, var18];
  };

  const isMappedValue = (value) => {
    return value && value !== "None" && value !== "- no Mapping -";
  };

  // Function to get available columns for a dropdown (excluding already selected ones)
  const getAvailableColumns = (currentFieldIndex = null, currentCustomColumnName = null) => {
    const currentValues = getAllVarValues();
    const usedColumns = new Set();

    currentValues.forEach((value, index) => {
      if (index !== currentFieldIndex && isMappedValue(value)) {
        usedColumns.add(value);
      }
    });

    Object.entries(customColumnsMapping).forEach(([columnName, value]) => {
      if (columnName !== currentCustomColumnName && isMappedValue(value)) {
        usedColumns.add(value);
      }
    });

    // Filter out used columns from selection
    return selection.filter(col => col === "- no Mapping -" || !usedColumns.has(col));
  };

  // Helper function to generate menu items with duplicate prevention
  const getMenuItemsForField = (fieldIndex = null, currentCustomColumnName = null) => {
    const availableColumns = getAvailableColumns(fieldIndex, currentCustomColumnName);
    return menuItems(availableColumns);
  };

  const updateCustomColumnsMapping = (name, value) => {
    setCustomColumnsMapping((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getCustomColumnName = (column) => (
    typeof column === "string" ? column : column.name
  );

  const getCustomColumnKey = (column, index) => (
    "custom_" + index + "_" + getCustomColumnName(column)
  );

  const normalizeColumnName = (name) => String(name || "").trim().toLowerCase();

  const getColumnWithMatchingName = (targetName) => {
    const normalizedTargetName = normalizeColumnName(targetName);
    if (!normalizedTargetName) {
      return null;
    }

    const matchingColumns = props.uploadFile.columns.filter(
      (column) => normalizeColumnName(column) === normalizedTargetName
    );
    return matchingColumns.length === 1 ? matchingColumns[0] : null;
  };

  const getRememberedSuggestion = (targetName, suggestions) => {
    const normalizedTargetName = normalizeColumnName(targetName);
    const matches = Object.entries(suggestions).filter(([fieldName]) => (
      normalizeColumnName(fieldName) === normalizedTargetName
    ));
    return matches.length === 1 ? matches[0][1] : null;
  };

  const getSuggestedColumn = (targetName, suggestions, suggestionKey = targetName) => (
    suggestions[suggestionKey]
    || getRememberedSuggestion(targetName, suggestions)
    || getColumnWithMatchingName(targetName)
  );

  const handleStateChange1 = (event, id) => {
    switch (id) {
      case "0":
        setVar0(event.target.value);
        break;
      case "1":
        setVar1(event.target.value);
        break;
      case "2":
        setVar2(event.target.value);
        break;
      case "3":
        setVar3(event.target.value);
        break;
      case "4":
        setVar4(event.target.value);
        break;
      case "5":
        setVar5(event.target.value);
        break;
      case "6":
        setVar6(event.target.value);
        break;
      case "7":
        setVar7(event.target.value);
        break;
      case "8":
        setVar8(event.target.value);
        break;
      case "9":
        setVar9(event.target.value);
        break;
      case "10":
        setVar10(event.target.value);
        break;
      case "11":
        setVar11(event.target.value);
        break;
      case "12":
        setVar12(event.target.value);
        break;
      case "13":
        setVar13(event.target.value);
        break;
      case "14":
        setVar14(event.target.value);
        break;
      case "15":
        setVar15(event.target.value);
        break;
      case "16":
        setVar16(event.target.value);
        break;
      case "17":
        setVar17(event.target.value);
        break;
      case "18":
        setVar18(event.target.value);
        break;
      default:
        break;
    }
  };

  const applyFixedSuggestions = (suggestions) => {
    const currentValues = getAllVarValues();
    const usedColumns = new Set(currentValues.filter(isMappedValue));
    let appliedCount = 0;

    Object.entries(mappingFields).forEach(([index, fieldName]) => {
      if (isMappedValue(currentValues[index]) || fieldName === "volumeUnit") {
        return;
      }

      const suggestedColumn = getSuggestedColumn(
        mappingFieldLabels[fieldName],
        suggestions,
        fieldName
      );
      if (suggestedColumn && !usedColumns.has(suggestedColumn)) {
        handleStateChange1({ target: { value: suggestedColumn } }, index);
        usedColumns.add(suggestedColumn);
        appliedCount++;
      }
    });

    return appliedCount;
  };

  // Function to load and apply mapping suggestions
  const loadMappingSuggestions = async () => {
    if (!props.uploadFile.columns || props.uploadFile.columns.length === 0) {
      return;
    }

    setLoadingSuggestions(true);
    let suggestions = {};
    try {
      const username = localStorage.getItem("user");
      if (!username) {
        console.log("No username found in localStorage");
        return;
      }

      const response = await axios.post(API_URL_MAPPING_SUGGESTIONS, {
        columns: props.uploadFile.columns,
        username: username
      });

      suggestions = response.data?.suggestions || {};
      const appliedCount = applyFixedSuggestions(suggestions);
      if (appliedCount > 0) {
        setShowSuggestionsAlert(true);
        console.log(`Applied ${appliedCount} mapping suggestions`);
      }
    } catch (error) {
      console.error("Error loading mapping suggestions:", error);
    } finally {
      setMappingSuggestions(suggestions);
      setSuggestionsLoaded(true);
      setSuggestionsApplied(true);
      setLoadingSuggestions(false);
    }
  };

  useEffect(() => {
    if (!suggestionsLoaded) {
      return;
    }

    setCustomColumnsMapping((previousMappings) => {
      const activeColumns = props.customColumns || [];
      const activeKeys = new Set(
        activeColumns.map((column, index) => getCustomColumnKey(column, index))
      );
      const nextMappings = Object.fromEntries(
        Object.entries(previousMappings).filter(([key]) => activeKeys.has(key))
      );
      let changed = Object.keys(nextMappings).length !== Object.keys(previousMappings).length;

      activeColumns.forEach((column, index) => {
        const columnName = getCustomColumnName(column);
        const columnKey = getCustomColumnKey(column, index);
        if (
          isMappedValue(nextMappings[columnKey])
          || manuallyChangedCustomMappings.current.has(columnKey)
        ) {
          return;
        }

        const suggestedColumn = getSuggestedColumn(columnName, mappingSuggestions);
        if (suggestedColumn) {
          nextMappings[columnKey] = suggestedColumn;
          changed = true;
        }
      });

      return changed ? nextMappings : previousMappings;
    });
  }, [props.customColumns, props.uploadFile.columns, mappingSuggestions, suggestionsLoaded]);

  // Function to save current mappings for future suggestions
  const saveCurrentMappings = async () => {
    try {
      const username = localStorage.getItem("user");
      if (!username) {
        return;
      }

      const currentValues = getAllVarValues();
      const mappings = {};

      // Build mappings object from current selections
      Object.entries(mappingFields).forEach(([index, fieldName]) => {
        const value = currentValues[parseInt(index)];
        if (value && value !== "- no Mapping -") {
          mappings[fieldName] = value;
        }
      });

      (props.customColumns || []).forEach((column, index) => {
        const value = customColumnsMapping[getCustomColumnKey(column, index)];
        if (isMappedValue(value)) {
          mappings[getCustomColumnName(column)] = value;
        }
      });

      if (Object.keys(mappings).length > 0) {
        await axios.post(API_URL_MAPPING_SAVE, {
          mappings: mappings,
          columns: props.uploadFile.columns,
          username: username
        });
      }
    } catch (error) {
      console.error("Error saving user mappings:", error);
    }
  };

  // Load suggestions when file columns are available
  useEffect(() => {
    if (
      props.uploadFile.columns &&
      props.uploadFile.columns.length > 0 &&
      !suggestionsApplied &&
      !props.mappingDataState
    ) {
      loadMappingSuggestions();
    }
  }, [props.uploadFile.columns, suggestionsApplied, props.mappingDataState]);

  // Restore mapping state from props if available
  useEffect(() => {
    if(props.mappingDataState){
      setVar0(props.mappingDataState?.var0 || "");
      setVar1(props.mappingDataState?.var1 || "");
      setVar2(props.mappingDataState?.var2 || "- no Mapping -");
      setVar3(props.mappingDataState?.var3 || "- no Mapping -");
      setVar4(props.mappingDataState?.var4 || "");
      setVar5(props.mappingDataState?.var5 || "");
      setVar6(props.mappingDataState?.var6 || "- no Mapping -");
      setVar7(props.mappingDataState?.var7 || "- no Mapping -");
      setVar8(props.mappingDataState?.var8 || "- no Mapping -");
      setVar9(props.mappingDataState?.var9 || "- no Mapping -");
      setVar10(props.mappingDataState?.var10 || "- no Mapping -");
      setVar11(props.mappingDataState?.var11 || "- no Mapping -");
      setVar12(props.mappingDataState?.var12 || "- no Mapping -");
      setVar13(props.mappingDataState?.var13 || "");
      setVar14(props.mappingDataState?.var14 || "- no Mapping -");
      setVar15(props.mappingDataState?.var15 || "- no Mapping -");
      setVar16(props.mappingDataState?.var16 || "- no Mapping -");
      setVar17(props.mappingDataState?.var17 || "- no Mapping -");
      setVar18(props.mappingDataState?.var18 || "- no Mapping -");
      setCustomColumnsMapping(props.mappingDataState?.customColumnsMapping || {});
      setSuggestionsApplied(true);
    } else {
      setCustomColumnsMapping({});
    }
  }, [props.mappingDataState]);

  const postMapping = async (final) => {
    // Save user mappings for future suggestions (do this before validation)
    await saveCurrentMappings();

    props.setMappingDataState({
      'var0':var0,
      'var1':var1,
      'var2':var2,
      'var3':var3,
      'var4':var4,
      'var5':var5,
      'var6':var6,
      'var7':var7,
      'var8':var8,
      'var9':var9,
      'var10':var10,
      'var11':var11,
      'var12':var12,
      'var13':var13,
      'var14':var14,
      'var15':var15,
      'var16':var16,
      'var17':var17,
      'var18':var18,
      'customColumnsMapping': customColumnsMapping,
    })
    // Upload file: post via axios endpoint and get resulting feedback
    let interDict = {};
    const varArray = [var0, var1, var2, var3, var4, var5, var6, var7, var8, var9, var10, var11, var12, var13, var14, var15, var16, var17, var18];
    for (let i = 0; i < 19; i++) {
      interDict[mappingFields[i]] = varArray[i];
    }
    const formData = {};
    const columnsChanged = [];
    for (const [key, value] of Object.entries(interDict)) {
      if (isMappedValue(value)) {
        formData[key] = value;
        columnsChanged.push(key);
      }
    }
    const customColumnMappingPayload = (props.customColumns || [])
      .map((column, index) => {
        const columnKey = getCustomColumnKey(column, index);
        const value = customColumnsMapping[columnKey];
        return isMappedValue(value) ? { [columnKey]: value } : null;
      })
      .filter(Boolean);

    formData["columns"] = columnsChanged;
    formData["rows"] = props.uploadFile.rows;
    formData["tenderID"] = props.tender;
    formData["additionalData"] = var16;
    formData["customColumnMapping"] = customColumnMappingPayload;
    
    // Send custom columns metadata (including required flags)
    formData["customColumnsMetadata"] = props.customColumns || [];
    
    const response = await axios.post(API_URL_MAPPING, formData);
    setTable(response.data);
    await props.func(response.data);
    setFileStatus(true);
    if (final) {
      props.handleNext();
    }
  };

  const gridStyle = React.useMemo(() => ({ height: 540, width: "100%" }), []);
  let fullScreenHandle = useFullScreenHandle();
  const gridRef = React.useRef();

  return (
    <Box>
      {/* Loading suggestions indicator */}
      {loadingSuggestions && (
        <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography>Loading field mapping suggestions...</Typography>
        </Box>
      )}
      
      {/* Suggestions applied alert */}
      {showSuggestionsAlert && (
        <Alert 
          severity="info" 
          onClose={() => setShowSuggestionsAlert(false)}
          sx={{ mb: 2 }}
        >
          Auto-suggested field mappings have been applied based on your previous usage. 
          You can modify any mapping as needed.
        </Alert>
      )}
      
      <Grid container spacing={2} justify="flex-end" alignItems="center">
        <Grid item xs={8}>
          <Typography align="left" sx={{ mt: 2, mb: 1 }}>
            <strong>
              Define how the columns correspond to the essential lane
              information.
            </strong>
            <br />
            Fields with the value '- no Mapping -' are optional fields which can
            be selected or left as is.
          </Typography>
        </Grid>
        <Grid item xs={4}></Grid>
        <Grid item xs={1}></Grid>
        <Grid item xs={2}>
          <Typography sx={{ mt: 2, mb: 1, fontSize: 14, color: "#a2232b" }}>
            Lane Information
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography sx={{ mt: 2, mb: 1, fontSize: 14, color: "#a2232b" }}>
            Input Column from File
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography sx={{ mt: 2, mb: 1, fontSize: 14, color: "#a2232b" }}>
            Lane Information
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography sx={{ mt: 2, mb: 1, fontSize: 14, color: "#a2232b" }}>
            Input Column from File
          </Typography>
        </Grid>
        <Grid item xs={3}></Grid>
        {/* Next line */}
        <Grid item xs={1}></Grid>
        <Grid item xs={2}>
          <Typography sx={{ mt: 2, mb: 1 }}>Origin Country</Typography>
        </Grid>
        <Grid item xs={2}>
          <FormControl error sx={{ width: "80%" }}>
            <InputLabel>Required</InputLabel>
            <Select
              value={String(var0)}
              label="Required"
              sx={{ width: "100%" }}
              onChange={(e) => handleStateChange1(e, "0")}
            >
              {getMenuItemsForField(0)}
            </Select>
          </FormControl>
        </Grid>
        <Divider orientation="vertical" flexItem />
        <Grid item xs={2}>
          <Typography sx={{ mt: 2, mb: 1 }}>Destination Country</Typography>
        </Grid>
        <Grid item xs={2}>
          <FormControl error sx={{ width: "80%" }}>
            <InputLabel>Required</InputLabel>
            <Select
              value={var1}
              label="Required"
              sx={{ width: "100%" }}
              onChange={(e) => handleStateChange1(e, "1")}
            >
              {getMenuItemsForField(1)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={2}></Grid>

        {/* Next line */}
        <Grid item xs={1}></Grid>
        <Grid item xs={2}>
          <Typography sx={{ mt: 2, mb: 1 }}>Origin ZIP</Typography>
        </Grid>
        <Grid item xs={2}>
          <FormControl error sx={{ width: "80%" }}>
            <InputLabel>Required</InputLabel>
            <Select
              value={var4}
              label="Required"
              sx={{ width: "100%" }}
              onChange={(e) => handleStateChange1(e, "4")}
            >
              {getMenuItemsForField(4)}
            </Select>
          </FormControl>
        </Grid>
        <Divider orientation="vertical" flexItem />
        <Grid item xs={2}>
          <Typography sx={{ mt: 2, mb: 1 }}>Destination ZIP</Typography>
        </Grid>
        <Grid item xs={2}>
          <FormControl error sx={{ width: "80%" }}>
            <InputLabel>Required</InputLabel>
            <Select
              value={var5}
              label="Required"
              sx={{ width: "100%" }}
              onChange={(e) => handleStateChange1(e, "5")}
            >
              {getMenuItemsForField(5)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={2}></Grid>

        {/* Next line */}
        <Grid item xs={1}></Grid>
        <Grid item xs={2}>
          <Typography sx={{ mt: 2, mb: 1 }}>Origin City</Typography>
        </Grid>
        <Grid item xs={2}>
          <Select
            value={var2}
            label=""
            sx={{ width: "80%" }}
            displayEmpty
            onChange={(e) => handleStateChange1(e, "2")}
          >
            {getMenuItemsForField(2)}
          </Select>
        </Grid>
        <Divider orientation="vertical" flexItem />
        <Grid item xs={2}>
          <Typography sx={{ mt: 2, mb: 1 }}>Destination City</Typography>
        </Grid>
        <Grid item xs={2}>
          <Select
            value={var3}
            label=""
            sx={{ width: "80%" }}
            onChange={(e) => handleStateChange1(e, "3")}
          >
            {getMenuItemsForField(3)}
          </Select>
        </Grid>
        <Grid item xs={2}></Grid>
      </Grid>

      <Box sx={{ m: 1 }} />
      <Divider />
      <Box sx={{ m: 1 }} />
      <Grid container spacing={2} justify="flex-end" alignItems="center">
        <Grid item xs={1}></Grid>
        <Grid item xs={2}>
          <Typography sx={{ mt: 2, mb: 1 }}>Equipment</Typography>
        </Grid>
        <Grid item xs={2}>
          <Select
            value={var6}
            label=""
            sx={{ width: "80%" }}
            onChange={(e) => handleStateChange1(e, "6")}
          >
            {getMenuItemsForField(6)}
          </Select>
        </Grid>
        <Divider orientation="vertical" flexItem />
        <Grid item xs={2}>
          <Typography sx={{ mt: 2, mb: 1 }}>Payload</Typography>
        </Grid>
        <Grid item xs={2}>
          <Select
            value={var7}
            label=""
            sx={{ width: "80%" }}
            // onChange={handleStateChange1}
            onChange={(e) => handleStateChange1(e, "7")}
          >
            {getMenuItemsForField(7)}
          </Select>
        </Grid>
        <Grid item xs={2}></Grid>
        {/* Next line */}
        <Grid item xs={1}></Grid>
        <Grid item xs={2}>
          <Typography sx={{ mt: 2, mb: 1 }}>Transit Time</Typography>
        </Grid>
        <Grid item xs={2}>
          <Select
            value={var8}
            label=""
            sx={{ width: "80%" }}
            // onChange={handleStateChange1}
            onChange={(e) => handleStateChange1(e, "8")}
          >
            {getMenuItemsForField(8)}
          </Select>
        </Grid>
        <Divider orientation="vertical" flexItem />
        <Grid item xs={2}>
          <Typography sx={{ mt: 2, mb: 1 }}>Pallet Exchange</Typography>
        </Grid>
        <Grid item xs={2}>
          <Select
            value={var9}
            label=""
            sx={{ width: "80%" }}
            // onChange={handleStateChange1}
            onChange={(e) => handleStateChange1(e, "9")}
          >
            {getMenuItemsForField(9)}
          </Select>
        </Grid>
        <Grid item xs={2}></Grid>
        {/* Next line */}
        <Grid item xs={1}></Grid>
        <Grid item xs={2}>
          <Typography sx={{ mt: 2, mb: 1 }}>Special Requirements</Typography>
        </Grid>
        <Grid item xs={2}>
          <Select
            value={var10}
            label=""
            sx={{ width: "80%" }}
            // onChange={handleStateChange1}
            onChange={(e) => handleStateChange1(e, "10")}
          >
            {getMenuItemsForField(10)}
          </Select>
        </Grid>
        <Divider orientation="vertical" flexItem />
        <Grid item xs={2}>
          <Typography sx={{ mt: 2, mb: 1 }}>Oneway/Roundtrip</Typography>
        </Grid>
        <Grid item xs={2}>
          <Select
            value={var11}
            label=""
            sx={{ width: "80%" }}
            // onChange={handleStateChange1}
            onChange={(e) => handleStateChange1(e, "11")}
          >
            {getMenuItemsForField(11)}
          </Select>
        </Grid>
        <Grid item xs={2}></Grid>
        {/* Next line */}
        <Grid item xs={1}></Grid>
        <Grid item xs={2}>
          <Typography sx={{ mt: 2, mb: 1 }}>
            Lane ID provided by Customer
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Select
            value={var12}
            label=""
            sx={{ width: "80%" }}
            // onChange={handleStateChange1}
            onChange={(e) => handleStateChange1(e, "12")}
          >
            {getMenuItemsForField(12)}
          </Select>
        </Grid>
        <Divider orientation="vertical" flexItem />
        <Grid item xs={2}>
          <Typography sx={{ mt: 2, mb: 1 }}>Custom Data</Typography>
        </Grid>
        <Grid item xs={2}>
          <Select
            error={checkCol.additional_data_required? true : false}
            value={var16}
            label={checkCol.additional_data_required ? 'Required': 'No mapping'}
            sx={{ width: "80%" }}
            // onChange={handleStateChange1}
            onChange={(e) => handleStateChange1(e, "16")}
          >
            {getMenuItemsForField(16)}
          </Select>
        </Grid>
        <Grid item xs={2}></Grid>
        {/* Next line */}
        <Grid item xs={1}></Grid>
        <Grid item xs={2}>
          <Typography sx={{ mt: 2, mb: 1 }}>Capacity</Typography>
        </Grid>
        <Grid item xs={2}>
          <Select
            value={var17}
            label=""
            sx={{ width: "80%" }}
            onChange={(e) => handleStateChange1(e, "17")}
          >
            {getMenuItemsForField(17)}
          </Select>
        </Grid>
        <Divider orientation="vertical" flexItem />
        <Grid item xs={2}>
          <Typography sx={{ mt: 2, mb: 1 }}>Register Sheet</Typography>
        </Grid>
        <Grid item xs={2}>
          <Select
            value={var18}
            label=""
            sx={{ width: "80%" }}
            onChange={(e) => handleStateChange1(e, "18")}
          >
            {getMenuItemsForField(18)}
          </Select>
        </Grid>
        <Grid item xs={2}></Grid>
        {/* Next line */}
      </Grid>
      <Box sx={{ m: 1 }} />

      {/*CUSTOM COLUMNS*/}
      {props.customColumns && props.customColumns.length > 0 && (
        <>
          <Divider />
          <Grid container spacing={2} justify="flex-end" alignItems="center">
            {
              props.customColumns.map((column, index) => {
                // Handle both old string format and new object format
                const columnName = getCustomColumnKey(column, index);
                const displayName = getCustomColumnName(column);

                const val = customColumnsMapping[columnName] || "- no Mapping -";
                return (
                  <React.Fragment key={columnName}>
                    {index % 2 === 0 ? (
                      <Grid item xs={1}></Grid>
                    ) : (
                      <Divider orientation="vertical" flexItem />
                    )}
                    <Grid item xs={2}>
                      <Typography sx={{ mt: 2, mb: 1 }}>{displayName}</Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Select
                        value={val}
                        label={displayName}
                        name={columnName}
                        sx={{ width: "80%" }}
                        onChange={(e) => {
                          manuallyChangedCustomMappings.current.add(columnName);
                          updateCustomColumnsMapping(columnName, e.target.value);
                        }}
                      >
                        {getMenuItemsForField(null, columnName)}
                      </Select>
                    </Grid>
                    {index + 1 === props.customColumns.length && (
                      <Grid item xs={3}></Grid>
                    )}
                    {index % 2 != 0 && <Grid item xs={2}></Grid>}
                  </React.Fragment>
                );
              })
              //INDEX EQUAL
            }
          </Grid>
          <Box sx={{ m: 1 }} />
          <Divider />
          <Box sx={{ m: 1 }} />
        </>
      )}
      <Grid container spacing={2} justify="flex-end" alignItems="center">
        <Grid item xs={1}></Grid>
        <Grid item xs={2}>
          <Typography sx={{ mt: 2, mb: 1 }}>Annual Volume + Unit</Typography>
        </Grid>
        <Grid item xs={2}>
          <FormControl sx={{ width: "80%" }}>
            <Select
              value={var13}
              label="Required"
              sx={{ width: "100%" }}
              onChange={(e) => handleStateChange1(e, "13")}
            >
              {getMenuItemsForField(13)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={2}>
          <Select
            value={var14}
            label=""
            sx={{ width: "80%" }}
            onChange={(e) => handleStateChange1(e, "14")}
          >
            <MenuItem value={"- no Mapping -"}>- no Mapping -</MenuItem>
            <MenuItem value={"Transports"}>Transports</MenuItem>
            <MenuItem value={"Tons"}>Tons</MenuItem>
            <MenuItem value={"m3"}>m^3</MenuItem>
            <MenuItem value={"Nr Palettes"}>Nr. Palettes</MenuItem>
          </Select>
        </Grid>
        <Grid item xs={4}></Grid>
        <Grid item xs={11} align="right" sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            size="large"
            onClick={() => postMapping(false)}
            color="inherit"
          >
            Preview
          </Button>
        </Grid>
        <Grid item xs={9} align="left"></Grid>

        <Grid item xs={0.5}></Grid>
        <Grid item xs={11}>
          {isPosted(tableData.missing) && (
            <Typography align="left" sx={{ mt: 2, mb: 1 }}>
              <strong>
                Some relevant columns are still missing. Please specifiy the
                columns in your file that contain the following lane
                information:
              </strong>
            </Typography>
          )}
        </Grid>
        <Grid item xs={0.5}></Grid>
        <Grid item xs={0.5}></Grid>

        <Grid item xs={12}>
          {isPosted(fileStatus) && (
            <FullScreen handle={fullScreenHandle}>
              <TableControls
                table={gridRef}
                fullScreenHandle={fullScreenHandle}
              />
              <div style={gridStyle} className="ag-theme-alpine">
                <AgGridReact
                  ref={gridRef} // Ref for accessing Grid's API
                  rowData={tableData.rows} // Row Data for Rows
                  columnDefs={enrichTableColumns(tableData)} // Column Defs for Columns
                  pagination={true}
                  paginationAutoPageSize={true}
                />
              </div>
            </FullScreen>
          )}
        </Grid>
        <Grid align="right" xs={12} sx={{ mt: 3 }}>
          {isPosted(fileStatus) && (
            <Button
              variant="contained"
              size="large"
              onClick={() => postMapping(true)}
            >
              Post Mapping
            </Button>
          )}
        </Grid>
        <Grid item xs={0.5}></Grid>
      </Grid>
    </Box>
  );
};
export default Mapping;
